import {
  drawTextCell,
  GridCellKind,
  measureTextCached,
  type CustomCell,
  type CustomRenderer,
  type ProvideEditorComponent,
} from "@glideapps/glide-data-grid";
import { Check, ChevronDown } from "lucide-react";
import { type KeyboardEvent, useEffect, useId, useRef, useState } from "react";
import { Stack } from "@/core/components/ui/stack/stack";
import { cn } from "@/core/utils/utils";

const SUGGESTED_VALUE_CELL_KIND = "suggested-value";
/** Chevron glyph width, and the gap kept between it and the cell text. */
const CHEVRON_WIDTH = 9;
const CHEVRON_GAP = 6;
const ELLIPSIS = "…";

/** Trailing space reserved for the chevron so cell text can never run under it. */
const controlGutterWidth = (theme: { cellHorizontalPadding: number }) =>
  theme.cellHorizontalPadding + CHEVRON_WIDTH + CHEVRON_GAP;

const measure = (ctx: CanvasRenderingContext2D, text: string, font: string) =>
  measureTextCached(text, ctx, font).width;

/**
 * Glide's own truncation is a character-count guess, so long values spill into
 * the chevron gutter. Cut to the measured width and mark the cut with an
 * ellipsis instead.
 */
const truncateToWidth = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
  font: string,
) => {
  if (measure(ctx, text, font) <= maxWidth) return text;

  let fits = 0;
  let limit = text.length;
  while (fits < limit) {
    const candidate = Math.ceil((fits + limit) / 2);
    if (
      measure(ctx, `${text.slice(0, candidate)}${ELLIPSIS}`, font) <= maxWidth
    )
      fits = candidate;
    else limit = candidate - 1;
  }
  return `${text.slice(0, fits).trimEnd()}${ELLIPSIS}`;
};

export interface SuggestedValueCellData {
  kind: typeof SUGGESTED_VALUE_CELL_KIND;
  value: string;
  suggestions: readonly string[];
}

export type SuggestedValueCell = CustomCell<SuggestedValueCellData>;

const isSuggestedValueCellData = (
  data: unknown,
): data is SuggestedValueCellData =>
  typeof data === "object" &&
  data != null &&
  "kind" in data &&
  data.kind === SUGGESTED_VALUE_CELL_KIND &&
  "value" in data &&
  typeof data.value === "string" &&
  "suggestions" in data &&
  Array.isArray(data.suggestions);

export const getSuggestedValueCellValue = (
  data: unknown,
): string | undefined =>
  isSuggestedValueCellData(data) ? data.value : undefined;

const withValue = (
  cell: SuggestedValueCell,
  value: string,
): SuggestedValueCell => ({
  ...cell,
  copyData: value,
  data: { ...cell.data, value },
});

export const SuggestedValueCellEditor: ProvideEditorComponent<
  SuggestedValueCell
> = ({
  initialValue,
  isHighlighted,
  onChange,
  onFinishedEditing,
  target,
  value,
}) => {
  const initialDraft = initialValue ?? value.data.value;
  const [draft, setDraft] = useState(initialDraft);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const appliedInitialValueRef = useRef(false);
  const listboxId = useId();

  useEffect(() => {
    inputRef.current?.focus();
    if (isHighlighted && initialValue === undefined) inputRef.current?.select();
  }, [initialValue, isHighlighted]);

  useEffect(() => {
    if (highlightedIndex >= 0)
      optionRefs.current[highlightedIndex]?.scrollIntoView?.({
        block: "nearest",
      });
  }, [highlightedIndex]);

  useEffect(() => {
    if (initialValue !== undefined && !appliedInitialValueRef.current) {
      appliedInitialValueRef.current = true;
      onChange(withValue(value, initialValue));
    }
  }, [initialValue, onChange, value]);

  const updateDraft = (nextValue: string) => {
    setDraft(nextValue);
    onChange(withValue(value, nextValue));
  };

  const finish = (
    nextValue: string,
    movement: readonly [-1 | 0 | 1, -1 | 0 | 1] = [0, 0],
  ) => onFinishedEditing(withValue(value, nextValue), movement);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setHighlightedIndex((current) =>
          Math.min(current + 1, value.data.suggestions.length - 1),
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        setHighlightedIndex((current) =>
          current < 0
            ? value.data.suggestions.length - 1
            : Math.max(current - 1, 0),
        );
        break;
      case "Enter":
        event.preventDefault();
        event.stopPropagation();
        finish(value.data.suggestions[highlightedIndex] ?? draft, [0, 1]);
        break;
      case "Tab":
        event.preventDefault();
        event.stopPropagation();
        finish(draft, event.shiftKey ? [-1, 0] : [1, 0]);
        break;
      case "Escape":
        event.preventDefault();
        event.stopPropagation();
        onFinishedEditing();
        break;
    }
  };

  return (
    // Glide's overlay already draws the editor's border, radius and shadow.
    // Adding our own would double-frame the popover, so only fill it in.
    <Stack
      gap="none"
      fullWidth
      className="overflow-hidden bg-card"
      style={{ minWidth: Math.max(target.width, 200) }}
    >
      <Stack
        direction="row"
        gap="none"
        align="center"
        fullWidth
        className="relative"
      >
        <input
          ref={inputRef}
          aria-label="Cell value"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={
            highlightedIndex >= 0
              ? `${listboxId}-option-${highlightedIndex}`
              : undefined
          }
          aria-expanded="true"
          role="combobox"
          value={draft}
          onChange={(event) => {
            setHighlightedIndex(-1);
            updateDraft(event.target.value);
          }}
          onKeyDown={handleKeyDown}
          // Match the grid's own text metrics and inset so the value neither
          // shifts nor resizes when the editor opens over the cell.
          style={{ height: Math.max(target.height, 32) }}
          className="w-full border-0 bg-card py-0 pl-2 pr-7 text-[13px] text-primary-text outline-none"
        />
        <ChevronDown
          aria-hidden
          strokeWidth={1.5}
          className="pointer-events-none absolute right-2 size-3.5 text-secondary-text"
        />
      </Stack>
      <Stack
        id={listboxId}
        role="listbox"
        gap="none"
        fullWidth
        className="max-h-64 overflow-y-auto border-t border-stroke-secondary p-1"
      >
        {value.data.suggestions.map((suggestion, index) => (
          <button
            key={`${index}-${suggestion}`}
            id={`${listboxId}-option-${index}`}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            type="button"
            role="option"
            aria-selected={highlightedIndex === index}
            className={cn(
              "flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-[13px] leading-5 text-primary-text hover:bg-bg-secondary",
              { "bg-bg-secondary": highlightedIndex === index },
            )}
            onMouseDown={(event) => {
              event.preventDefault();
              finish(suggestion);
            }}
            onMouseEnter={() => setHighlightedIndex(index)}
          >
            <span className="truncate">{suggestion}</span>
            {suggestion === draft && (
              <Check aria-hidden className="size-3.5 shrink-0" />
            )}
          </button>
        ))}
      </Stack>
    </Stack>
  );
};

export const suggestedValueCellRenderer: CustomRenderer<SuggestedValueCell> = {
  kind: GridCellKind.Custom,
  isMatch: (cell): cell is SuggestedValueCell =>
    isSuggestedValueCellData(cell.data),
  needsHover: true,
  draw: (args, cell) => {
    const { ctx, rect, theme, hoverAmount } = args;
    const textWidth = Math.max(0, rect.width - controlGutterWidth(theme));
    const availableTextWidth = textWidth - theme.cellHorizontalPadding * 2;

    if (cell.data.value !== "" && availableTextWidth > 0) {
      // Clip as well as truncate: the ellipsis keeps the text readable, the
      // clip guarantees nothing bleeds into the chevron gutter.
      ctx.save();
      ctx.beginPath();
      ctx.rect(rect.x, rect.y, textWidth, rect.height);
      ctx.clip();
      drawTextCell(
        { ...args, rect: { ...rect, width: textWidth } },
        truncateToWidth(
          ctx,
          cell.data.value,
          availableTextWidth,
          theme.baseFontFull,
        ),
        cell.contentAlign,
      );
      ctx.restore();
    }

    const centerX =
      rect.x + rect.width - theme.cellHorizontalPadding - CHEVRON_WIDTH / 2;
    const centerY = Math.round(rect.y + rect.height / 2);
    ctx.save();
    ctx.globalAlpha = 0.7 + 0.3 * hoverAmount;
    ctx.beginPath();
    ctx.moveTo(centerX - CHEVRON_WIDTH / 2, centerY - 1.5);
    ctx.lineTo(centerX, centerY + 2.5);
    ctx.lineTo(centerX + CHEVRON_WIDTH / 2, centerY - 1.5);
    ctx.lineWidth = 1.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = theme.textMedium;
    ctx.stroke();
    ctx.restore();
  },
  measure: (ctx, cell, theme) =>
    measure(ctx, cell.data.value, theme.baseFontFull) +
    theme.cellHorizontalPadding * 2 +
    controlGutterWidth(theme),
  provideEditor: () => ({
    editor: SuggestedValueCellEditor,
    disablePadding: true,
  }),
  onPaste: (pastedValue, data) => ({
    ...data,
    value: pastedValue,
  }),
};

export const buildSuggestedValueGridCell = ({
  value,
  suggestions,
  contentAlign,
  themeOverride,
}: {
  value: string;
  suggestions: readonly string[];
  contentAlign?: "left" | "right" | "center";
  themeOverride?: SuggestedValueCell["themeOverride"];
}): SuggestedValueCell => ({
  kind: GridCellKind.Custom,
  allowOverlay: true,
  readonly: false,
  copyData: value,
  contentAlign,
  themeOverride,
  activationBehaviorOverride: "single-click",
  data: {
    kind: SUGGESTED_VALUE_CELL_KIND,
    value,
    suggestions,
  },
});
